import { listBlobs } from '~/storage/blobStore'
import { ALL_STORE_NAMES, clearAllStoresIn, type StoreCounts } from '~/storage/clearAll'
import type { SolaDatabase } from '~/storage/db'
import type { StorageError } from '~/storage/errors'
import { notifyWritten } from '~/storage/invalidation'
import { asSchemaAgnostic, countAllStores } from '~/storage/readAll'
import { getLastBackupAt } from '~/storage/repositories/metaRepository'
import { type MetaKey, STORE, type StoreName } from '~/storage/schema'
import { withTransaction } from '~/storage/transactions'
import type { Result } from '~/utils/result'
import { ok } from '~/utils/result'

/**
 * Full reset — remove everything Project Sola stores on this device (D-067).
 *
 * **What this deliberately does not touch**, each recorded rather than inferred:
 *
 *   - **OPFS recovery snapshots** (D-067A). They live in a separate database and
 *     exist to be the last copy of data a *restore* replaced. Wiping them here
 *     would destroy a safety net the user is not being asked about, on a screen
 *     whose whole message is that recovery needs a backup file. D-030 retention
 *     remains their only lifecycle.
 *   - **Cache Storage and the service-worker registration** (D-067B). The
 *     precached shell holds no user data — `runtimeCaching` is empty and D-049
 *     keeps records in IndexedDB — so clearing it would break offline launch
 *     and force a re-download for no privacy gain. The app stays installed.
 *   - **No pre-reset snapshot is written** (D-067D). D-031's "the safety
 *     snapshot is never silently skipped" governs restore, where destruction is
 *     a side effect of an operation with another purpose. Here destruction *is*
 *     the purpose, chosen twice, with a backup offered and declinable. A
 *     snapshot would also make the confirmation copy false.
 */

/**
 * Meta keys a full reset preserves. **Empty by D-067(C)** — a full reset clears
 * `meta` including `lastBackupAt`.
 *
 * D-046 says `lastBackupAt` "must survive a settings reset". That decision is an
 * argument about *where the key lives* (the operational `meta` store, not user
 * settings), and the narrower settings-only reset it describes does not exist as
 * a feature — recorded as L-013. Keeping the date through a full reset would
 * show "Last backup: 5 August" above an empty database, asserting a protection
 * the user does not have. That is the exact failure `backupService` was written
 * to avoid.
 *
 * Kept as data so reversing the decision is one line here plus one expectation
 * in the test.
 */
export const RESET_PRESERVED_META_KEYS: readonly MetaKey[] = []

/** What a reset would remove. Read from the live database, never estimated. */
export interface ResetPreview {
  readonly counts: StoreCounts
  readonly totalRecords: number
  readonly blobCount: number
  readonly blobBytes: number
  readonly lastBackupAt: string | undefined
}

export interface ResetOutcome {
  readonly recordsRemoved: number
  readonly blobsRemoved: number
}

/**
 * Read what a reset would remove. **Mutates nothing** — the confirmation the
 * user reads is rendered from this, so it must be safe to call before they have
 * decided anything.
 */
export async function previewReset(db: SolaDatabase): Promise<Result<ResetPreview, StorageError>> {
  const counts = await countAllStores(asSchemaAgnostic(db))

  const blobs = await listBlobs(db)
  if (!blobs.ok) return blobs

  const lastBackupAt = await getLastBackupAt(db)
  if (!lastBackupAt.ok) return lastBackupAt

  const blobBytes = blobs.value.reduce((sum, entry) => sum + (entry.byteSize ?? entry.blob.size), 0)

  // Blobs are counted separately and excluded from the record total so the two
  // figures shown to the user do not double-count the same thing.
  const totalRecords = Object.entries(counts).reduce(
    (sum, [store, count]) => (store === STORE.blobs ? sum : sum + count),
    0,
  )

  return ok({
    counts,
    totalRecords,
    blobCount: blobs.value.length,
    blobBytes,
    lastBackupAt: lastBackupAt.value,
  })
}

/**
 * Remove everything, atomically.
 *
 * Preserved meta keys are read before the clear and re-written after it, inside
 * the same transaction, so a failure anywhere leaves the database untouched
 * rather than half-reset. With `RESET_PRESERVED_META_KEYS` empty this loop does
 * nothing today; it exists so reversing D-067(C) does not require rewriting the
 * transaction.
 */
export async function resetAllData(db: SolaDatabase): Promise<Result<ResetOutcome, StorageError>> {
  const written = await withTransaction(db, [...ALL_STORE_NAMES], async (tx) => {
    const metaStore = tx.objectStore(STORE.meta)
    const preserved: { key: string; value: unknown }[] = []
    for (const key of RESET_PRESERVED_META_KEYS) {
      const row = await metaStore.get(key)
      if (row !== undefined) preserved.push(row)
    }

    const counts = await clearAllStoresIn(tx)

    for (const row of preserved) {
      await tx.objectStore(STORE.meta).put(row)
    }

    return counts
  })

  if (!written.ok) return written

  // Everything changed. The same broadcast the restore path makes, so any
  // mounted screen refetches instead of rendering records that no longer exist.
  notifyWritten(...(ALL_STORE_NAMES as StoreName[]))

  const removed = Object.entries(written.value).reduce(
    (sum, [store, count]) => (store === STORE.blobs ? sum : sum + count),
    0,
  )

  return ok({
    recordsRemoved: removed,
    blobsRemoved: written.value[STORE.blobs] ?? 0,
  })
}
